import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Send } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { GuildChannelSelect } from '@/components/dashboard/GuildChannelSelect';
import { GuildRoleSelect } from '@/components/dashboard/GuildRoleSelect';
import { ToggleRow } from '@/components/ui/ToggleRow';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { SaveBar } from '@/components/ui/SaveBar';
import { Skeleton } from '@/components/ui/Skeleton';
import { useGuildConfig } from '@/hooks/useGuildConfig';
import { ModuleTogglePanel } from '@/components/dashboard/ModuleTogglePanel';

const PLACEHOLDER_HELP = "{user} and {member_count} are supported, matching the bot's own placeholder engine.";

function renderPreview(template: string, username: string, memberCount: number) {
  return template.replaceAll('{user}', username || 'someone').replaceAll('{member_count}', String(memberCount));
}

/** Matches welcome_settings exactly (bridge/config_registry.py's "welcome"
 * module) — ids are strings throughout, never numbers (Discord snowflakes
 * exceed JS's safe integer range; the Bridge already returns them as
 * strings for this reason — see config_registry.stringify_snowflakes). */
interface WelcomeData {
  enabled: boolean;
  welcome_channel: string | null;
  leave_channel: string | null;
  welcome_message: string | null;
  leave_message: string | null;
  autorole: string | null;
  use_embed: boolean;
}

export function WelcomePage() {
  const { guildId } = useParams<{ guildId: string }>();
  const { state, current, dirty, saving, saveError, resetting, update, save, discard, reset } =
    useGuildConfig<WelcomeData>(guildId, 'welcome');

  const joinPreview = useMemo(
    () => renderPreview(current?.welcome_message ?? '', 'ExampleUser', 1248),
    [current?.welcome_message],
  );
  const leavePreview = useMemo(
    () => renderPreview(current?.leave_message ?? '', 'ExampleUser', 1247),
    [current?.leave_message],
  );

  if (state.kind === 'loading') {
    return (
      <div className="space-y-6">
        <Card><Skeleton className="h-24 w-full" /></Card>
        <Card><Skeleton className="h-24 w-full" /></Card>
      </div>
    );
  }

  if (state.kind === 'forbidden') {
    return <Alert tone="danger" title="You don't have permission to view this" description="Welcome settings require Administrator." />;
  }

  if (state.kind === 'unavailable' || !current) {
    return <Alert tone="danger" title="Couldn't load welcome settings" description={state.kind === 'unavailable' ? state.message : 'Something went wrong.'} />;
  }

  return (
    <div className="space-y-6 pb-24">
      <ModuleTogglePanel module="welcome" title="Enable welcome messages" masterLabel="Enable welcome/leave messages" permissionNote="Administrator permission required." />

      {saveError && <Alert tone="danger" title="Couldn't save" description={saveError} />}

      <Card>
        <CardHeader title="Channels" description="Where join/leave messages get posted." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-small text-text-secondary block mb-1">Welcome channel</label>
            <GuildChannelSelect
              guildId={guildId}
              value={current.welcome_channel}
              onChange={(id) => update({ welcome_channel: id })}
              aria-label="Welcome channel"
            />
          </div>
          <div>
            <label className="text-small text-text-secondary block mb-1">Leave channel</label>
            <GuildChannelSelect
              guildId={guildId}
              value={current.leave_channel}
              onChange={(id) => update({ leave_channel: id })}
              aria-label="Leave channel"
            />
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Join message" description={PLACEHOLDER_HELP} />
        <Input
          value={current.welcome_message ?? ''}
          onChange={(e) => update({ welcome_message: e.target.value || null })}
          aria-label="Join message template"
          maxLength={2000}
        />
        <div className="mt-3 bg-bg-base border border-border-subtle rounded-sm p-3">
          <p className="text-micro text-text-tertiary uppercase mb-1">Live preview</p>
          <p className="text-small text-text-primary">{joinPreview || '(empty)'}</p>
        </div>
      </Card>

      <Card>
        <CardHeader title="Leave message" />
        <Input
          value={current.leave_message ?? ''}
          onChange={(e) => update({ leave_message: e.target.value || null })}
          aria-label="Leave message template"
          maxLength={2000}
        />
        <div className="mt-3 bg-bg-base border border-border-subtle rounded-sm p-3">
          <p className="text-micro text-text-tertiary uppercase mb-1">Live preview</p>
          <p className="text-small text-text-primary">{leavePreview || '(empty)'}</p>
        </div>
      </Card>

      <Card>
        <CardHeader title="Auto-role" description="Role granted automatically on join." />
        <GuildRoleSelect
          guildId={guildId}
          value={current.autorole}
          onChange={(id) => update({ autorole: id })}
          aria-label="Auto-role"
        />
      </Card>

      <Card>
        <CardHeader title="Delivery" />
        <ToggleRow
          title="Send as embed"
          description="Otherwise sent as a plain message."
          checked={current.use_embed}
          onChange={(v) => update({ use_embed: v })}
        />
        <div className="pt-3">
          <Button variant="secondary" disabled>
            <Send size={16} aria-hidden="true" />
            Send test message
          </Button>
        </div>
      </Card>

      <SaveBar visible={dirty || saving} onSave={save} onDiscard={discard} saving={saving} />
      {!dirty && (
        <button type="button" onClick={reset} disabled={resetting} className="text-small text-text-tertiary hover:text-text-secondary underline underline-offset-2 disabled:opacity-50">
          {resetting ? 'Resetting…' : 'Reset to defaults'}
        </button>
      )}
    </div>
  );
}
